/**
 * Meeting Provider Context
 *
 * This context manages the meeting provider selection (Dyte vs LiveKit).
 * It allows components to determine which provider is currently in use.
 */

import React, { createContext, useContext, ReactNode } from 'react';

export type MeetingProvider = 'dyte' | 'livekit';

interface MeetingProviderContextValue {
  provider: MeetingProvider;
}

const MeetingProviderContext = createContext<MeetingProviderContextValue | undefined>(undefined);

interface MeetingProviderProviderProps {
  children: ReactNode;
  provider: MeetingProvider;
}

/**
 * Provider component that wraps the app and provides the meeting provider type
 */
export const MeetingProviderProvider: React.FC<MeetingProviderProviderProps> = ({
  children,
  provider,
}) => {
  return (
    <MeetingProviderContext.Provider value={{ provider }}>
      {children}
    </MeetingProviderContext.Provider>
  );
};

/**
 * Hook to access the current meeting provider
 * @returns The current meeting provider ('dyte' or 'livekit')
 * @throws Error if used outside of MeetingProviderProvider
 */
export const useMeetingProvider = (): MeetingProviderContextValue => {
  const context = useContext(MeetingProviderContext);
  if (context === undefined) {
    throw new Error('useMeetingProvider must be used within MeetingProviderProvider');
  }
  return context;
};

/**
 * Helper function to determine provider from URL parameters
 * @param searchParams URLSearchParams object
 * @returns 'dyte' or 'livekit'
 */
export const getProviderFromURLParams = (searchParams: URLSearchParams): MeetingProvider => {
  const providerParam = searchParams.get('provider');

  if (providerParam === 'livekit') {
    return 'livekit';
  }

  // Default to dyte for backward compatibility
  return 'dyte';
};
